// Import components
import React, { useEffect, useState } from "react";
import Breadcrumbs from '../../../Components/Common/Breadcrumb';
import { Card, CardBody, Row, Col, Alert, Input, Button, Label, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import axios from "axios";
import OrderCancellationModal from '../../../Components/custom/OrderCancellationModal';
import OrderTransactionHistoryExpander from '../../../Components/custom/OrderTransactionHistoryExpander';

const AdminOrders = () => {

    const [showloader, setshowloader] = useState(true);
    const [alertMsgg, setAlertMsgg] = useState<any>({ open: false, type: "success", content: "Messages here.", time: "2500" });

    const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") { return; }
        setAlertMsgg({ ...alertMsgg, open: false });
    };

    const [orders, setOrders] = useState<any[]>([]);
    const [analytics, setAnalytics] = useState<any>({
        total_orders: 0,
        paid_orders: 0,
        pending_orders: 0,
        total_revenue: 0,
        average_order_value: 0,
        refunded_orders: 0,
        failed_orders: 0,
    });
    const [filters, setFilters] = useState({
        status: 'all',
        search: '',
        date_from: '',
        date_to: '',
    });
    const [cancellationModal, setCancellationModal] = useState<{ open: boolean; order: any | null }>({
        open: false,
        order: null,
    });

    const fetchOrders = async () => {
        try {
            setshowloader(true);
            const response = await axios.post(
                `/payments/admin/orders/`,
                filters,
                { withCredentials: true }
            );
            if (response && response.status === "SENT") {
                setOrders(response.orders);
                setAnalytics(response.analytics);
            }
            setshowloader(false);
        } catch (error: any) {
            console.error("Error fetching orders:", error);
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Access denied or session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    }

    useEffect(() => {   
        fetchOrders();
    }, []);

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        
        // Auto-apply filters when status radio button changes
        if (name === 'status') {
            const newFilters = { ...filters, [name]: value };
            applyFiltersWithData(newFilters);
        }
    };

    const applyFiltersWithData = async (filtersToApply: typeof filters) => {
        try {
            setshowloader(true);
            const response = await axios.post(
                `/payments/admin/orders/`,
                filtersToApply,
                { withCredentials: true }
            );
            if (response && response.status === "SENT") {
                setOrders(response.orders);
                setAnalytics(response.analytics);
            }
            setshowloader(false);
        } catch (error: any) {
            console.error("Error fetching orders:", error);
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Access denied or session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    const handleApplyFilters = () => {
        fetchOrders();
    };

    const handleClearFilters = () => {
        setFilters({
            status: 'all',
            search: '',
            date_from: '',
            date_to: '',
        });
        setTimeout(() => fetchOrders(), 100);
    };

    const handleOpenCancellation = (order: any) => {
        setCancellationModal({ open: true, order });
    };

    const handleCloseCancellation = () => {
        setCancellationModal({ open: false, order: null });
    };

    const handleCancellationSuccess = () => {
        // Refresh orders after successful cancellation
        fetchOrders();
        // Success message is already shown in the modal with details
    };

    const handleExportToCSV = () => {
        if (orders.length === 0) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "No orders to export", type: "warning" });
            return;
        }

        // Prepare CSV content
        let csvContent = "Order ID,UUID,User,Email,Total Price,Status,Created At,Paid At,Purchases Count\n";
        
        orders.forEach(order => {
            const row = [
                order.order_id,
                order.order_uuid,
                order.user?.username || 'N/A',
                order.user?.email || 'N/A',
                order.total_price?.toFixed(2) || '0.00',
                order.payment_status,
                formatDate(order.created_at),
                order.paid_at ? formatDate(order.paid_at) : 'N/A',
                order.purchases_count
            ].map(field => `"${field}"`).join(',');
            csvContent += row + "\n";
        });

        // Create and download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `orders_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setAlertMsgg({ ...alertMsgg, open: true, content: "CSV exported successfully!", type: "success" });
    };

    document.title = "All Orders | Admin | BizServe";

    return (
        <div className="page-content">
            {alertMsgg.open && (
                <Alert
                    color={alertMsgg.type === "success" ? "success" : alertMsgg.type === "warning" ? "warning" : "danger"}
                    toggle={handleAlertMsggClose}
                    fade={false}
                    style={{ fontWeight: "bold" }}
                >
                    {alertMsgg.content}
                </Alert>
            )}
            <div className="loaderContainer" hidden={!showloader}>
                <div className="spinner">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            </div>
            <div className="container-fluid px-0 px-md-3 py-3" style={{ minHeight: '90vh' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs title="Admin" breadcrumbItem="All Orders" />
                    <Button 
                        color="info" 
                        className="btn-rounded"
                        onClick={() => window.location.href = '/acancellations'}
                    >
                        <i className="bx bx-x-circle me-2"></i>
                        View Cancellations
                    </Button>
                </div>
                
                {/* Analytics Cards */}
                <Row className="mb-4">
                    <Col xl={3} md={6} className="mb-3 mb-xl-0">
                        <Card className="rounded-4 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #5558d8 0%, #5a3d83 100%)' }}>
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <p className="text-white mb-1 fw-semibold" style={{ fontSize: 13, opacity: 0.9 }}>Total Orders</p>
                                        <h3 className="mb-0 text-white fw-bold">{analytics.total_orders}</h3>
                                    </div>
                                    <div className="avatar-sm">
                                        <span className="avatar-title bg-white bg-opacity-25 rounded-circle text-white">
                                            <i className="bx bx-shopping-bag font-size-24"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3} md={6} className="mb-3 mb-xl-0">
                        <Card className="rounded-4 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #d946a6 0%, #d63854 100%)' }}>
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <p className="text-white mb-1 fw-semibold" style={{ fontSize: 13, opacity: 0.9 }}>Total Revenue</p>
                                        <h3 className="mb-0 text-white fw-bold">${analytics.total_revenue?.toFixed(2) || '0.00'}</h3>
                                    </div>
                                    <div className="avatar-sm">
                                        <span className="avatar-title bg-white bg-opacity-25 rounded-circle text-white">
                                            <i className="bx bx-dollar-circle font-size-24"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3} md={6} className="mb-3 mb-xl-0">
                        <Card className="rounded-4 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)' }}>
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <p className="text-white mb-1 fw-semibold" style={{ fontSize: 13, opacity: 0.9 }}>Paid Orders</p>
                                        <h3 className="mb-0 text-white fw-bold">{analytics.paid_orders}</h3>
                                    </div>
                                    <div className="avatar-sm">
                                        <span className="avatar-title bg-white bg-opacity-25 rounded-circle text-white">
                                            <i className="bx bx-check-circle font-size-24"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3} md={6} className="mb-3 mb-xl-0">
                        <Card className="rounded-4 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)' }}>
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <p className="text-white mb-1 fw-semibold" style={{ fontSize: 13, opacity: 0.9 }}>Avg Order Value</p>
                                        <h3 className="mb-0 text-white fw-bold">${analytics.average_order_value?.toFixed(2) || '0.00'}</h3>
                                    </div>
                                    <div className="avatar-sm">
                                        <span className="avatar-title bg-white bg-opacity-25 rounded-circle text-white">
                                            <i className="bx bx-trending-up font-size-24"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Filters and Orders Section */}
                <div className="row g-4">
                    {/* Filters Card */}
                    <div className="col-12 col-lg-3">
                        <Card className="rounded-4 border-0 shadow-sm bg-white">
                            <CardBody className="p-4">
                                <div className="mb-3 fw-bold fs-5 text-primary-emphasis d-flex align-items-center">
                                    <i className="bx bx-filter-alt me-2"></i>
                                    Filters
                                </div>
                                
                                <div className="mb-3">
                                    <Label className="form-label fw-semibold d-block mb-2">Order Status</Label>
                                    <div className="d-flex flex-column gap-2">
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="status" 
                                                id="status_all" 
                                                value="all"
                                                checked={filters.status === 'all'}
                                                onChange={handleFilterChange}
                                            />
                                            <label className="form-check-label" htmlFor="status_all">
                                                All Orders
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="status" 
                                                id="status_paid" 
                                                value="paid"
                                                checked={filters.status === 'paid'}
                                                onChange={handleFilterChange}
                                            />
                                            <label className="form-check-label" htmlFor="status_paid">
                                                Paid
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="status" 
                                                id="status_pending" 
                                                value="pending"
                                                checked={filters.status === 'pending'}
                                                onChange={handleFilterChange}
                                            />
                                            <label className="form-check-label" htmlFor="status_pending">
                                                Pending
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="status" 
                                                id="status_failed" 
                                                value="failed"
                                                checked={filters.status === 'failed'}
                                                onChange={handleFilterChange}
                                            />
                                            <label className="form-check-label" htmlFor="status_failed">
                                                Failed
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="status" 
                                                id="status_refund" 
                                                value="refund"
                                                checked={filters.status === 'refund'}
                                                onChange={handleFilterChange}
                                            />
                                            <label className="form-check-label" htmlFor="status_refund">
                                                Refunded
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <Label htmlFor="search" className="form-label fw-semibold">Search</Label>
                                    <Input 
                                        type="text" 
                                        id="search"
                                        name="search"
                                        className="form-control" 
                                        placeholder="Order ID, User, Email..." 
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="mb-3">
                                    <Label htmlFor="date_from" className="form-label fw-semibold">Date From</Label>
                                    <Input 
                                        type="date" 
                                        id="date_from"
                                        name="date_from"
                                        className="form-control" 
                                        value={filters.date_from}
                                        onChange={handleFilterChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="mb-3">
                                    <Label htmlFor="date_to" className="form-label fw-semibold">Date To</Label>
                                    <Input 
                                        type="date" 
                                        id="date_to"
                                        name="date_to"
                                        className="form-control" 
                                        value={filters.date_to}
                                        onChange={handleFilterChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="d-flex gap-2 flex-column">
                                    <Button 
                                        color="primary" 
                                        className="w-100 btn-rounded"
                                        onClick={handleApplyFilters}
                                    >
                                        <i className="bx bx-search me-1"></i> Apply Filters
                                    </Button>
                                    <Button 
                                        color="secondary" 
                                        outline
                                        className="w-100 btn-rounded"
                                        onClick={handleClearFilters}
                                    >
                                        <i className="bx bx-x me-1"></i> Clear
                                    </Button>
                                </div>

                                <hr className="my-4" />

                                <div className="mb-2">
                                    <small className="text-muted fw-semibold d-block mb-2">Additional Stats</small>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Pending:</span>
                                        <span className="badge bg-warning text-dark">{analytics.pending_orders}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Refunded:</span>
                                        <span className="badge bg-info">{analytics.refunded_orders}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">Failed:</span>
                                        <span className="badge bg-danger">{analytics.failed_orders}</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Orders Card */}
                    <div className="col-12 col-lg-9">
                        <Card className="rounded-4 border-0 shadow-lg bg-white" style={{ minHeight: 600 }}>
                            <CardBody className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div>
                                        <h5 className="mb-1 fw-bold">All Orders</h5>
                                        <p className="text-muted mb-0">Total: {orders.length} orders</p>
                                    </div>
                                    <Button 
                                        color="success" 
                                        className="btn-rounded"
                                        onClick={handleExportToCSV}
                                        disabled={orders.length === 0}
                                    >
                                        <i className="bx bx-download me-1"></i> Export CSV
                                    </Button>
                                </div>

                                {orders && orders.length === 0 && !showloader ? (
                                    <div className="text-center py-5">
                                        <div className="avatar-lg mx-auto mb-4">
                                            <div className="avatar-title bg-soft-primary text-primary rounded-circle">
                                                <i className="bx bx-shopping-bag font-size-40"></i>
                                            </div>
                                        </div>
                                        <h5 className="text-muted">No orders found</h5>
                                        <p className="text-muted">Try adjusting your filters</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                                        {orders && orders.map((order: any, idx: number) => (
                                            <div key={order.order_id || idx} className="mb-4 p-4 rounded-4 shadow-sm order-card" style={{ background: 'linear-gradient(120deg, #f3f4f6 60%, #e5e7eb 100%)', border: '1px solid #e5e7eb' }}>
                                                {/* Order Header */}
                                                <div className="d-flex flex-wrap align-items-start justify-content-between mb-3 pb-3 border-bottom">
                                                    <div className="mb-2">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <span className="fw-bold me-2 text-dark" style={{ fontSize: 16 }}>Order #{order.order_id}</span>
                                                            <span className={`badge px-3 py-2 ${getStatusColor(order.payment_status)}`}>
                                                                {formatOrderStatus(order.payment_status)}
                                                            </span>
                                                            {order.refunded && (
                                                                <span className="badge bg-warning text-dark ms-2 px-2 py-1">Refunded</span>
                                                            )}
                                                        </div>
                                                        <div className="text-muted small">
                                                            UUID: <span className="font-monospace">{order.order_uuid}</span>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="d-flex align-items-start gap-3">
                                                        <div className="text-end">
                                                            <div className="fw-bold text-success fs-4">${order.total_price?.toFixed(2) ?? '0.00'}</div>
                                                            <div className="text-muted small">
                                                                Placed: {formatDate(order.created_at)}
                                                            </div>
                                                            {order.paid_at && (
                                                                <div className="text-muted small">
                                                                    Paid: {formatDate(order.paid_at)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {order.payment_status === 'paid' && (
                                                            <UncontrolledDropdown>
                                                                <DropdownToggle tag="button" className="btn btn-sm btn-light" caret>
                                                                    <i className="bx bx-dots-vertical-rounded"></i>
                                                                </DropdownToggle>
                                                                <DropdownMenu end>
                                                                    <DropdownItem onClick={() => handleOpenCancellation(order)}>
                                                                        <i className="bx bx-x-circle text-danger me-2"></i>
                                                                        Process Cancellation
                                                                    </DropdownItem>
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Customer Info */}
                                                {order.user && (
                                                    <div className="mb-3 p-3 rounded bg-white">
                                                        <div className="row g-3">
                                                            <div className="col-md-4">
                                                                <small className="text-muted d-block">Customer</small>
                                                                <strong>{order.user.full_name || order.user.username}</strong>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <small className="text-muted d-block">Email</small>
                                                                <span>{order.user.email}</span>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <small className="text-muted d-block">Transaction ID</small>
                                                                <span className="font-monospace small">{order.transaction_id || 'N/A'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Purchases Table */}
                                                {order.purchases && order.purchases.length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm align-middle mb-0" style={{ fontSize: 14 }}>
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>Product</th>
                                                                    <th>Description</th>
                                                                    <th>Type</th>
                                                                    <th>Partner</th>
                                                                    <th className="text-center">Qty</th>
                                                                    <th className="text-end">Unit Price</th>
                                                                    <th className="text-end">Total</th>
                                                                    <th>Validity End</th>
                                                                    <th className="text-center">Active</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {order.purchases.map((purchase: any, pidx: number) => (
                                                                    <tr key={purchase.purchase_id || pidx}>
                                                                        <td className={purchase.product_type === 'plan' ? 'fw-bold text-dark' : 'text-dark'}>
                                                                            {purchase.product_title}
                                                                            {purchase.domain && (
                                                                                <div className="text-secondary" style={{ fontSize: 11, fontWeight: 400, textTransform: 'capitalize' }}>
                                                                                    <i className="bx bx-folder me-1"></i>{purchase.domain}
                                                                                </div>
                                                                            )}
                                                                            {(() => {
                                                                                // Check if product is a subscription based on validity field
                                                                                const validity = purchase.validity;
                                                                                const isSubscriptionProduct = validity && validity !== '' && validity !== 'lifetime' && validity !== 'custom' && ['plan', 'addon', 'subscriptionaddon', 'planoptionals'].includes(purchase.product_type);
                                                                                const isActive = purchase.curr_prd_end ? new Date(purchase.curr_prd_end) >= new Date() : purchase.pack_active;
                                                                                
                                                                                if (purchase.is_cancelled) {
                                                                                    return (
                                                                                        <div className="mt-1">
                                                                                            <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>
                                                                                                <i className="bx bx-info-circle me-1"></i>
                                                                                                Subscription Inactive
                                                                                            </span>
                                                                                        </div>
                                                                                    );
                                                                                } else if (isSubscriptionProduct && isActive) {
                                                                                    return (
                                                                                        <div className="mt-1">
                                                                                            <span className="badge bg-success text-white" style={{ fontSize: 10 }}>
                                                                                                <i className="bx bx-check-circle me-1"></i>
                                                                                                Subscription Active
                                                                                            </span>
                                                                                        </div>
                                                                                    );
                                                                                }
                                                                                return null;
                                                                            })()}
                                                                        </td>
                                                                        <td className="text-muted small" style={{ position: 'relative' }}>
                                                                            {purchase.description && purchase.description.length > 50 ? (
                                                                                <>
                                                                                    <span>{purchase.description.substring(0, 50)}</span>
                                                                                    <span
                                                                                        style={{ color: '#1976d2', cursor: 'pointer' }}
                                                                                        onMouseEnter={e => {
                                                                                            const tip = document.getElementById(`purchase-desc-tip-${order.order_id}-${pidx}`);
                                                                                            if (tip) tip.style.display = 'block';
                                                                                        }}
                                                                                        onMouseLeave={e => {
                                                                                            const tip = document.getElementById(`purchase-desc-tip-${order.order_id}-${pidx}`);
                                                                                            if (tip) tip.style.display = 'none';
                                                                                        }}
                                                                                    >
                                                                                        {' '}...more
                                                                                    </span>
                                                                                    <div
                                                                                        id={`purchase-desc-tip-${order.order_id}-${pidx}`}
                                                                                        style={{
                                                                                            display: 'none',
                                                                                            position: 'absolute',
                                                                                            top: '100%',
                                                                                            left: '50%',
                                                                                            transform: 'translateX(-50%)',
                                                                                            marginTop: 8,
                                                                                            background: '#222',
                                                                                            color: '#fff',
                                                                                            padding: 10,
                                                                                            borderRadius: 8,
                                                                                            fontSize: 12,
                                                                                            zIndex: 10,
                                                                                            minWidth: 200,
                                                                                            maxWidth: 300,
                                                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                                                            whiteSpace: 'normal',
                                                                                            wordBreak: 'break-word',
                                                                                        }}
                                                                                    >
                                                                                        {purchase.description}
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                purchase.description || '-'
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {purchase.product_type && (
                                                                                <span className="badge bg-info text-dark">
                                                                                    {PRODUCT_TYPE_LABELS[purchase.product_type] || purchase.product_type}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {purchase.partner ? (
                                                                                <div>
                                                                                    <span className="badge bg-primary text-white">
                                                                                        {purchase.partner.partner_name || 'N/A'}
                                                                                    </span>
                                                                                    {purchase.partner.partner_email && (
                                                                                        <div className="text-muted small" style={{ fontSize: 11 }}>
                                                                                            {purchase.partner.partner_email}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-muted small">-</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <span className="badge bg-secondary">{purchase.quantity || 1}</span>
                                                                        </td>
                                                                        <td className="text-muted text-end">${purchase.unit_price?.toFixed(2) ?? '0.00'}</td>
                                                                        <td className="fw-bold text-primary text-end">${purchase.total_price?.toFixed(2) ?? '0.00'}</td>
                                                                        <td>
                                                                            {purchase.curr_prd_end ? (
                                                                                <span className="badge bg-light text-dark">{formatDate(purchase.curr_prd_end)}</span>
                                                                            ) : '-'}
                                                                        </td>
                                                                        <td className="text-center">
                                                                            {(() => {
                                                                                // Check if service is still active based on end date
                                                                                if (purchase.curr_prd_end) {
                                                                                    const endDate = new Date(purchase.curr_prd_end);
                                                                                    const isActive = endDate >= new Date();
                                                                                    return (
                                                                                        <i className={`bi ${isActive ? 'bx bxs-check-circle text-success' : 'bx bxs-x-circle text-danger'}`} style={{ fontSize: 18 }}></i>
                                                                                    );
                                                                                }
                                                                                // If no end date, check pack_active as fallback
                                                                                return (
                                                                                    <i className={`bi ${purchase.pack_active ? 'bx bxs-check-circle text-success' : 'bx bxs-x-circle text-danger'}`} style={{ fontSize: 18 }}></i>
                                                                                );
                                                                            })()}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="text-muted small text-center py-3">No purchases in this order.</div>
                                                )}

                                            {/* Order-Level Transaction History Expander (Admin view) */}
                                            {order.purchases && order.purchases.some((p: any) => p.stripe_subscription_id) && (
                                                <OrderTransactionHistoryExpander
                                                    orderId={order.order_id}
                                                    totalAmount={order.total_price}
                                                    productCount={order.purchases.filter((p: any) => p.stripe_subscription_id).length}
                                                />
                                            )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Cancellation Modal */}
            <OrderCancellationModal
                isOpen={cancellationModal.open}
                toggle={handleCloseCancellation}
                order={cancellationModal.order}
                isAdmin={true}
                onSuccess={handleCancellationSuccess}
            />
        </div>
    );
};

export default AdminOrders;

// Helper functions for formatting
function formatDate(dateStr: string) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatOrderStatus(status: string) {
    switch (status) {
        case 'paid': return 'Paid';
        case 'pending': return 'Pending';
        case 'failed': return 'Failed';
        case 'refund': return 'Refunded';
        default: return status;
    }
}

// Add getStatusColor helper for colorful status badges
function getStatusColor(status: string) {
    switch (status) {
        case 'paid': return 'bg-success bg-gradient text-white';
        case 'pending': return 'bg-warning bg-gradient text-dark';
        case 'failed': return 'bg-danger bg-gradient text-white';
        case 'refund': return 'bg-info bg-gradient text-white';
        default: return 'bg-secondary';
    }
}

// Map for product_type display names
const PRODUCT_TYPE_LABELS: Record<string, string> = {
    plan: 'Plan/Package',
    planoptionals: 'Optional',
    addon: 'Add-On',
    subscriptionaddon: 'Subscription',
    onetimeproduct: 'Product/Service',
};

